import React, { useState } from 'react';
import { TextField, Typography } from '@material-ui/core';
import styled from 'styled-components';
import BasicModal from 'components/common/modal/BasicModal';
import Button from 'components/common/Button';
import { retryPaymentByInvoice } from 'api/plansales/invoice';

interface Props {
  invoiceId: string;
  refetchInvoice: () => void;
}

const InvoiceDetailPaymentRetryModal = ({ invoiceId, refetchInvoice }: Props) => {
  const [isRetryModalOpen, setIsRetryModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const retry = async () => {
    setErrorMessage('');
    setIsLoading(true);
    retryPaymentByInvoice({ invoiceId, comment: comment })
      .then(() => {
        refetchInvoice();
        onClose();
      })
      .catch(e =>
        setErrorMessage(
          e?.response?.data
            ? e?.response?.data?.resultData
            : `An Error Occurred. ${JSON.stringify(e)}`,
        ),
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onClose = () => {
    setIsRetryModalOpen(false);
    setErrorMessage('');
    setComment('');
  };

  return (
    <ModalWrapper>
      <Button
        onClick={() => setIsRetryModalOpen(true)}
        size={'small'}
        color={'default'}
        variant={'outlined'}
      >
        Try Payment
      </Button>
      <BasicModal
        open={isRetryModalOpen}
        onClose={onClose}
        title={'Try Payment'}
        maxWidth={'sm'}
        actionButton={
          <ButtonGroups>
            <Button variant={'outlined'} size={'small'} color={'default'} onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant={'contained'}
              size={'small'}
              color={'primary'}
              disabled={!comment}
              onClick={retry}
              isLoading={isLoading}
            >
              Confirm
            </Button>
          </ButtonGroups>
        }
      >
        <ContentWrapper>
          <Typography variant={'subtitle1'}>
            <b>Important: </b>
            User will be notified of the payment via email.
            <br />
            Are you sure you want to proceed?
          </Typography>
          <Typography variant={'body1'}>
            Comment <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            placeholder={'Please enter the reason for the retry'}
            fullWidth
            multiline
            rows={4}
            variant={'outlined'}
            value={comment}
            onChange={e => {
              setComment(e.target.value);
            }}
            inputProps={{ maxLength: 500 }}
          />
          {errorMessage && (
            <Typography variant={'body1'} color={'error'}>
              {errorMessage}
            </Typography>
          )}
        </ContentWrapper>
      </BasicModal>
    </ModalWrapper>
  );
};

export default InvoiceDetailPaymentRetryModal;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ModalWrapper = styled.div`
  display: inline-flex;
  padding: 0 5px;
`;
const ButtonGroups = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 8px;
  margin: 6px 0;
`;
